import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

import Loading from '$app-web/components/loading';
import { ON_PREMISE_INSTANCES } from '$app-web/constants';
import { useReadStudyBasicInfo } from '$app-web/hooks/use-study';
import { languages, store as appI18nStore } from '$app-web/i18n';
import LayoutFullWithAppBar from '$app-web/layouts/full-with-app-bar';
import AppBar from '$app-web/modules/auth/components/app-bar';
import AuthContentWrapper from '$app-web/modules/auth/components/auth-content-wrapper';
import AuthLayoutWrapper from '$app-web/modules/auth/components/auth-layout-wrapper';
import PageForgotPassword from '$app-web/modules/auth/pages/forgot-password';
import PageForgotPasswordSuccess from '$app-web/modules/auth/pages/forgot-password-success';
import PageResetPassword from '$app-web/modules/auth/pages/reset-password';
import PageSignIn from '$app-web/modules/auth/pages/sign-in';
import PageSignUp from '$app-web/modules/auth/pages/sign-up';
import PageWelcome from '$app-web/modules/auth/pages/welcome';
import { AUTH_ACTIONS } from '$app-web/spaces/dashboard/constants';
import * as dashboardStore from '$app-web/spaces/dashboard/store';
import { getEnvironmentName, URLsManager } from '$app-web/utils';

const ModuleAuth = () => {
    const dispatch = useDispatch();

    const [ready, setReady] = useState(false);

    const hasAuthAction = useSelector(dashboardStore.dashboardDuck.duckSelectors.hasAuthActionSelector);
    const authAction = useSelector(dashboardStore.dashboardDuck.duckSelectors.authActionSelector);

    const i18nNamespace = useSelector(appI18nStore.i18nDuck.duckSelectors.namespaceSelector);
    const i18nLanguage = useSelector(appI18nStore.i18nDuck.duckSelectors.languageSelector);

    const changeNamespace = useCallback(
        (namespace) => dispatch(appI18nStore.i18nDuck.duckActionCreators.changeNamespace(namespace)),
        [dispatch]
    );

    const {
        readStudyBasicInfo,
        isLoading: isReadStudyBasicInfoLoading,
        isError: isReadStudyBasicInfoError,
        data: studyBasicInfo,
        hasStudyBasicInfo,
    } = useReadStudyBasicInfo(authAction?.params?.studyId, () => {
        if (!ready) {
            setReady(true);
        }
    });

    const changeLanguage = useCallback(
        (languageCode) => dispatch(appI18nStore.i18nDuck.duckActionCreators.changeLanguage(languageCode)),
        [dispatch]
    );

    const isEDENInstance = useMemo(() => {
        return getEnvironmentName() === ON_PREMISE_INSTANCES.EDEN;
    }, []);

    useLayoutEffect(() => {
        // Auth strings are in pdash namespace. So change it if not current namespace.
        if (i18nNamespace !== 'pdash') {
            changeNamespace('pdash');
        }
        // If user logged out from PDash with a language other than en-US change the language.
        if (i18nLanguage !== languages.localeToLanguage.fr && isEDENInstance) {
            changeLanguage(languages.localeToLanguage.fr);
        } else if (i18nLanguage !== languages.localeToLanguage.en && !isEDENInstance) {
            changeLanguage(languages.localeToLanguage.en);
        }
    }, [changeLanguage, changeNamespace, i18nLanguage, i18nNamespace, isEDENInstance]);

    useLayoutEffect(() => {
        if (!hasAuthAction || authAction.type !== AUTH_ACTIONS.JOIN_STUDY) {
            setReady(true);
        } else if (authAction.type === AUTH_ACTIONS.JOIN_STUDY) {
            readStudyBasicInfo();
        }
    }, [authAction.params, authAction.type, hasAuthAction, readStudyBasicInfo]);

    if (!ready) {
        return (
            <Loading
                variant="parent-overlay"
                loading={isReadStudyBasicInfoLoading}
                error={isReadStudyBasicInfoError}
                errorHasButton
            />
        );
    }

    return (
        <LayoutFullWithAppBar
            wrapper={AuthLayoutWrapper}
            wrapperProps={{ studyBasicInfo }}
            appBar={<AppBar hasStudyBasicInfo={hasStudyBasicInfo} />}
            content={
                <AuthContentWrapper>
                    <Switch>
                        <Route
                            path={URLsManager.to('dashboard:auth:WELCOME')}
                            component={() => (
                                <PageWelcome studyBasicInfo={studyBasicInfo} hasStudyBasicInfo={hasStudyBasicInfo} />
                            )}
                        />
                        <Route path={URLsManager.to('dashboard:auth:SIGN_IN')} component={PageSignIn} />
                        <Route
                            exact
                            path={[URLsManager.to('dashboard:auth:SIGN_UP'), URLsManager.to('dashboard:auth:SIGN_UP_ROLE')]}
                            component={() => <PageSignUp hasStudyBasicInfo={hasStudyBasicInfo} />}
                        />
                        <Route exact path={URLsManager.to('dashboard:auth:FORGOT_PASSWORD')} component={PageForgotPassword} />
                        <Route
                            exact
                            path={URLsManager.to('dashboard:auth:FORGOT_PASSWORD_SUCCESS')}
                            component={PageForgotPasswordSuccess}
                        />
                        <Route exact path={URLsManager.to('dashboard:auth:RESET_PASSWORD')} component={PageResetPassword} />
                        <Route render={() => <Redirect to={URLsManager.to('dashboard:auth:WELCOME')} />} />
                    </Switch>
                </AuthContentWrapper>
            }
        />
    );
};

ModuleAuth.propTypes = {};

export default ModuleAuth;
